import { signOut } from "firebase/auth";
import { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useNavigate } from "react-router-dom";
import auth from "../../firebase.init";
import axios from "axios";

const SERVER_URL = "https://allinoneai-d1a5a1b54c64.herokuapp.com";

const NavBar = () => {
  const [user] = useAuthState(auth);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [navData, setNavData] = useState(null);

  // Cache keys
  const CACHE_KEYS = {
    AUTH_STATE: "app_auth_state",
    USER_DATA: "app_user_data",
  };

  // --- FETCH NAVBAR DATA ---
  useEffect(() => {
    const fetchNavData = async () => {
      try {
        const response = await axios.get(`${SERVER_URL}/api/navbar-v5`);
        if (response.data.success) {
          setNavData(response.data.data);
        }
      } catch (err) {
        setError("Failed to load navbar data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchNavData();
  }, []);

  // HANDLE AUTH STATE & USER DATA (Preserved)
  useEffect(() => {
    if (!loading) {
      try {
        const authStateData = {
          hasUser: !!user,
          timestamp: Date.now(),
          userEmail: user?.email || null,
        };
        localStorage.setItem(
          CACHE_KEYS.AUTH_STATE,
          JSON.stringify(authStateData)
        );
      } catch (error) {
        console.error("Error caching auth state:", error);
      }
    }
  }, [user, loading, CACHE_KEYS.AUTH_STATE]);

  const handleSignout = async () => {
    try {
      await signOut(auth);
      Object.values(CACHE_KEYS).forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error(`Error clearing ${key} from localStorage:`, error);
        }
      });
      setUserData(null);
      setMobileMenuVisible(false);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuVisible(!mobileMenuVisible);
  };

  // Handle smooth scrolling for hash links
  const handleHashClick = (e, url) => {
    if (url.startsWith('#')) {
      if (window.location.pathname !== '/') {
        // If not on homepage, let the browser navigate normally but maybe to /#about
        return;
      }
      
      const id = url.substring(1);
      const element = document.getElementById(id);
      if (element) {
        e.preventDefault();
        const headerOffset = 90; // Adjust for sticky header height
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
        
        // Update URL hash without jumping
        window.history.pushState(null, null, url);
        setMobileMenuVisible(false);
      }
    }
  };

  // Fetch user data with caching (Preserved)
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.email) {
        setUserData(null);
        try {
          localStorage.removeItem(CACHE_KEYS.USER_DATA);
        } catch (error) {
          console.error("Error clearing cached user data:", error);
        }
        return;
      }
      try {
        const response = await fetch(
          `${SERVER_URL}/users?userEmail=${user.email}`
        );
        const data = await response.json();
        if (data && data.length > 0) {
          const userData = data[0];
          setUserData(userData);
          try {
            localStorage.setItem(
              CACHE_KEYS.USER_DATA,
              JSON.stringify(userData)
            );
          } catch (error) {
            console.error("Error caching user data:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    if (user?.email) {
      const cachedUserData = localStorage.getItem(CACHE_KEYS.USER_DATA);
      const shouldFetch = !cachedUserData || !userData;
      if (shouldFetch) {
        fetchUserData();
      } else if (cachedUserData && !userData) {
        setUserData(JSON.parse(cachedUserData));
      }
    }
  }, [user, CACHE_KEYS.USER_DATA, userData]);

  // --- ROLE BASED LOGIC ---
  const getDashboardConfig = () => {
    if (userData?.userRole === "Admin") {
      return { link: "/admin/dashboard", text: "Admin Dashboard" };
    }
    return { link: "/user/dashboard", text: "Dashboard" };
  };

  const dashboardConfig = getDashboardConfig();

  if (loading)
    return <div className="container text-center py-4 text-white">Loading Navbar...</div>;
  
  // Fallback to default logo if API fails or inactive
  const logoLight = navData?.logo?.light || "/assets/img/logo/white-logo.svg";
  const activeLinks = navData?.navLinks?.sort((a, b) => a.order - b.order) || [];

  return (
    <>
      {/* pp Back To Top Start */}
      <button id="pp-back-top" className="pp-back-to-top show">
        <i className="fa-solid fa-arrow-up" />
      </button>
      
      {/* Mobile Offcanvas Area Start */}
      <div className={`mobile-offcanvas-wrapper ${mobileMenuVisible ? "menu-open" : ""}`}>
        {/* Dark overlay */}
        <div className="mobile-overlay" onClick={() => setMobileMenuVisible(false)}></div>
        
        {/* Sidebar */}
        <div className="mobile-sidebar d-flex flex-column h-100 position-relative overflow-hidden">
          
          {/* Subtle glowing backgrounds inside sidebar */}
          <div className="position-absolute top-0 end-0 bg-primary opacity-20 rounded-circle blur-glow z-n1" style={{ width: "200px", height: "200px", transform: "translate(50%, -50%)" }}></div>
          <div className="position-absolute bottom-0 start-0 bg-info opacity-10 rounded-circle blur-glow z-n1" style={{ width: "250px", height: "250px", transform: "translate(-50%, 50%)" }}></div>
          
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center p-4 border-bottom border-white-10">
            <Link to="/" onClick={() => setMobileMenuVisible(false)} className="mobile-logo position-relative">
              <div className="position-absolute top-50 start-50 translate-middle bg-primary rounded-circle blur-glow z-n1" style={{ width: "60px", height: "30px", opacity: "0.3" }}></div>
              <img src={logoLight} alt="logo" style={{ maxHeight: "35px" }} />
            </Link>
            <button className="mobile-close-btn d-flex align-items-center justify-content-center rounded-circle" onClick={() => setMobileMenuVisible(false)}>
              <i className="fas fa-times" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="mobile-content flex-grow-1 overflow-auto p-4">
            <p className="text-white-50 mb-4 small" style={{ lineHeight: "1.6" }}>
              {navData?.offcanvasDescription || "Elevating your content creation with AI."}
            </p>

            <nav className="mobile-nav mb-5">
              <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
                {activeLinks.map((link) => {
                  const isHash = link.url.startsWith('#');
                  return isHash ? (
                    <li key={`mobile-${link.id}`}>
                      <a 
                        href={link.url} 
                        onClick={(e) => handleHashClick(e, link.url)}
                        className={`mobile-nav-link d-block rounded-3 px-3 py-3 text-decoration-none ${window.location.hash === link.url ? "active" : ""}`}
                      >
                        {link.label}
                      </a>
                    </li>
                  ) : (
                    <li key={`mobile-${link.id}`}>
                      <Link 
                        to={link.url} 
                        onClick={() => setMobileMenuVisible(false)}
                        className={`mobile-nav-link d-block rounded-3 px-3 py-3 text-decoration-none ${link.url === window.location.pathname ? "active" : ""}`}
                      >
                        {link.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="mobile-auth-actions mb-5">
              {user ? (
                <div className="d-flex flex-column gap-3">
                  <Link 
                    to={dashboardConfig.link} 
                    onClick={() => setMobileMenuVisible(false)}
                    className="premium-btn w-100 text-center rounded-pill py-3 text-white fw-bold text-decoration-none position-relative overflow-hidden"
                  >
                    <span className="position-relative z-1">{dashboardConfig.text}</span>
                    <div className="btn-glow-bg position-absolute w-100 h-100 top-0 start-0 z-0"></div>
                  </Link>
                  <button 
                    onClick={() => { setMobileMenuVisible(false); handleSignout(); }}
                    className="btn btn-outline-danger w-100 rounded-pill py-3 fw-bold"
                    style={{ background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.2)", transition: "all 0.3s ease" }}
                  >
                    Logout
                  </button>
                </div>
              ) : (
                navData?.ctaButton?.visible && (
                  <Link 
                    to={navData.ctaButton.url} 
                    onClick={() => setMobileMenuVisible(false)}
                    className="premium-btn w-100 text-center rounded-pill py-3 text-white fw-bold text-decoration-none position-relative overflow-hidden d-block"
                  >
                    <span className="position-relative z-1">{navData.ctaButton.text}</span>
                    <div className="btn-glow-bg position-absolute w-100 h-100 top-0 start-0 z-0"></div>
                  </Link>
                )
              )}
            </div>

            <div className="mobile-contact-info mt-auto pt-4 border-top border-white-10">
              <h6 className="text-white fw-bold mb-4">Contact Info</h6>
              <ul className="list-unstyled d-flex flex-column gap-3">
                {navData?.contactInfo?.address && (
                  <li className="d-flex align-items-start gap-3">
                    <div className="contact-icon-wrapper rounded-circle d-flex align-items-center justify-content-center flex-shrink-0">
                      <i className="fal fa-map-marker-alt text-primary" />
                    </div>
                    <span className="text-white-50 small mt-1">{navData.contactInfo.address}</span>
                  </li>
                )}
                {navData?.contactInfo?.email && (
                  <li className="d-flex align-items-start gap-3">
                    <div className="contact-icon-wrapper rounded-circle d-flex align-items-center justify-content-center flex-shrink-0">
                      <i className="fal fa-envelope text-primary" />
                    </div>
                    <a href={`mailto:${navData.contactInfo.email}`} className="text-white-50 small mt-1 text-decoration-none">{navData.contactInfo.email}</a>
                  </li>
                )}
                {navData?.contactInfo?.phone && (
                  <li className="d-flex align-items-start gap-3">
                    <div className="contact-icon-wrapper rounded-circle d-flex align-items-center justify-content-center flex-shrink-0">
                      <i className="far fa-phone text-primary" />
                    </div>
                    <a href={`tel:${navData.contactInfo.phone}`} className="text-white-50 small mt-1 text-decoration-none">{navData.contactInfo.phone}</a>
                  </li>
                )}
              </ul>
              
              {navData?.socialLinks && navData.socialLinks.length > 0 && (
                <div className="d-flex align-items-center gap-2 mt-4 pt-2">
                  {navData.socialLinks.map((social) => (
                    <a key={social.id} href={social.url} className="social-btn d-flex align-items-center justify-content-center rounded-circle text-white text-decoration-none">
                      <i className={social.icon} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Header Section Start */}
      <header className="premium-header position-fixed top-0 w-100 z-3">
        {/* Edge-Lit Bottom Border */}
        <div className="position-absolute bottom-0 start-0 w-100" style={{ height: "1px", background: "linear-gradient(90deg, transparent 0%, rgba(99, 102, 241, 0.4) 50%, transparent 100%)" }}></div>
        
        <div className="container-fluid px-4 px-lg-5">
          <div className="d-flex align-items-center justify-content-between py-3">
            
            {/* Logo */}
            <div className="logo position-relative">
              <div className="position-absolute top-50 start-50 translate-middle bg-primary rounded-circle blur-glow z-n1" style={{ width: "80px", height: "30px", opacity: "0.2" }}></div>
              <Link to="/" className="d-block">
                {/* Forcing Light Logo for Dark Premium Theme */}
                <img src={logoLight} alt="logo-img" style={{ maxHeight: "35px" }} />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="d-none d-xl-block">
              <nav className="desktop-nav d-flex align-items-center gap-4">
                {activeLinks.map((link) => {
                  const isHash = link.url.startsWith('#');
                  return isHash ? (
                    <a key={link.id} href={link.url} onClick={(e) => handleHashClick(e, link.url)} className={`desktop-nav-link text-decoration-none fw-semibold position-relative ${window.location.hash === link.url ? "active" : ""}`}>
                      {link.label}
                    </a>
                  ) : (
                    <Link key={link.id} to={link.url} className={`desktop-nav-link text-decoration-none fw-semibold position-relative ${link.url === window.location.pathname ? "active" : ""}`}>
                      {link.label}
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Right Actions */}
            <div className="d-flex align-items-center gap-3">
              <div className="d-none d-md-flex align-items-center gap-3">
                {user ? (
                  <>
                    <Link to={dashboardConfig.link} className="premium-btn position-relative d-inline-flex align-items-center justify-content-center px-4 py-2 rounded-pill text-white fw-bold text-decoration-none overflow-hidden" style={{ fontSize: "0.9rem" }}>
                      <span className="position-relative z-1 d-flex align-items-center">
                        {dashboardConfig.text} <i className="fa-solid fa-arrow-right-long ms-2" />
                      </span>
                      <div className="btn-glow-bg position-absolute w-100 h-100 top-0 start-0 z-0"></div>
                    </Link>
                    <button onClick={handleSignout} className="btn text-white fw-bold border-0" style={{ fontSize: "0.9rem", transition: "color 0.3s ease" }} onMouseOver={(e) => e.target.style.color="#ef4444"} onMouseOut={(e) => e.target.style.color="white"}>
                      Logout
                    </button>
                  </>
                ) : (
                  navData?.ctaButton?.visible && (
                    <Link to={navData.ctaButton.url} className="premium-btn position-relative d-inline-flex align-items-center justify-content-center px-4 py-2 rounded-pill text-white fw-bold text-decoration-none overflow-hidden" style={{ fontSize: "0.9rem" }}>
                      <span className="position-relative z-1 d-flex align-items-center">
                        {navData.ctaButton.text} <i className="fa-solid fa-arrow-right-long ms-2" />
                      </span>
                      <div className="btn-glow-bg position-absolute w-100 h-100 top-0 start-0 z-0"></div>
                    </Link>
                  )
                )}
              </div>

              {/* Hamburger Button */}
              <button className="hamburger-btn d-xl-none d-flex flex-column justify-content-center gap-1 border-0 bg-transparent p-2" onClick={() => setMobileMenuVisible(true)}>
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
              </button>
            </div>

          </div>
        </div>
      </header>

      <style jsx>{`
        /* Global & Utility */
        .blur-glow { filter: blur(20px); }
        .border-white-10 { border-color: rgba(255,255,255,0.1) !important; }

        /* Premium Header */
        .premium-header {
          background: rgba(10, 10, 15, 0.7);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          transition: all 0.3s ease;
        }

        /* Desktop Nav Links */
        .desktop-nav-link {
          color: rgba(255,255,255,0.7);
          font-size: 0.95rem;
          letter-spacing: 0.02em;
          transition: all 0.3s ease;
          padding: 0.5rem 0;
        }
        .desktop-nav-link:hover, .desktop-nav-link.active {
          color: #fff;
        }
        .desktop-nav-link::after {
          content: '';
          position: absolute;
          width: 0;
          height: 2px;
          bottom: 0;
          left: 50%;
          background: #818cf8;
          transition: all 0.3s ease;
          transform: translateX(-50%);
          border-radius: 2px;
          box-shadow: 0 0 8px rgba(129, 140, 248, 0.6);
        }
        .desktop-nav-link:hover::after, .desktop-nav-link.active::after {
          width: 100%;
        }

        /* Premium Button */
        .premium-btn {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.1);
          box-shadow: 0 5px 15px rgba(99, 102, 241, 0.15);
          transition: all 0.3s ease;
        }
        .premium-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(99, 102, 241, 0.3), 0 0 15px rgba(139, 92, 246, 0.3);
          border-color: rgba(255,255,255,0.3);
        }
        .btn-glow-bg {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          opacity: 0.8;
          transition: opacity 0.3s ease;
        }
        .premium-btn:hover .btn-glow-bg {
          opacity: 1;
        }

        /* Hamburger */
        .hamburger-btn { cursor: pointer; }
        .hamburger-line {
          width: 24px; height: 2px;
          background-color: #fff;
          transition: all 0.3s ease;
          border-radius: 2px;
        }
        .hamburger-btn:hover .hamburger-line { background-color: #818cf8; box-shadow: 0 0 8px rgba(129, 140, 248, 0.6); }

        /* Mobile Offcanvas */
        .mobile-offcanvas-wrapper {
          position: fixed;
          top: 0; left: 0; width: 100%; height: 100vh;
          z-index: 9999;
          visibility: hidden;
        }
        .mobile-offcanvas-wrapper.menu-open {
          visibility: visible;
        }
        
        .mobile-overlay {
          position: absolute;
          top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(5px);
          opacity: 0;
          transition: opacity 0.4s ease;
        }
        .mobile-offcanvas-wrapper.menu-open .mobile-overlay { opacity: 1; }

        .mobile-sidebar {
          width: 320px;
          max-width: 85%;
          background: rgba(12, 12, 18, 0.85);
          backdrop-filter: blur(40px);
          -webkit-backdrop-filter: blur(40px);
          border-right: 1px solid rgba(255,255,255,0.05);
          transform: translateX(-100%);
          transition: transform 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
        }
        .mobile-offcanvas-wrapper.menu-open .mobile-sidebar { transform: translateX(0); }

        .mobile-close-btn {
          width: 36px; height: 36px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          color: #fff;
          transition: all 0.3s ease;
        }
        .mobile-close-btn:hover {
          background: rgba(239, 68, 68, 0.2);
          border-color: rgba(239, 68, 68, 0.4);
          color: #ef4444;
          transform: rotate(90deg);
        }

        .mobile-nav-link {
          color: rgba(255,255,255,0.7);
          font-weight: 600;
          letter-spacing: 0.05em;
          transition: all 0.3s ease;
          border: 1px solid transparent;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          color: #fff;
          background: rgba(99, 102, 241, 0.1);
          border-color: rgba(99, 102, 241, 0.2);
          transform: translateX(5px);
        }

        .contact-icon-wrapper {
          width: 36px; height: 36px;
          background: rgba(99, 102, 241, 0.08);
          border: 1px solid rgba(99, 102, 241, 0.15);
        }

        .social-btn {
          width: 36px; height: 36px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
        }
        .social-btn:hover {
          background: rgba(99, 102, 241, 0.2);
          border-color: rgba(99, 102, 241, 0.4);
          color: #818cf8 !important;
          transform: translateY(-3px);
        }
      `}</style>
    </>
  );
};

export default NavBar;